import axios from 'axios';

const LINK_KEY = 'pp.v2.link';
const DISPLAY_KEY = 'pp.v2.display';
const SESSION_KEY = 'pp.v2.session';
const DISPLAY_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const DISPLAY_LENGTH = 6;

const client = axios.create({ baseURL: process.env.API_PLATFORM_V2_URL });

const randomDisplayId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(DISPLAY_LENGTH)))
    .map((n) => DISPLAY_ALPHABET[n % DISPLAY_ALPHABET.length])
    .join('');

export const rememberLink = (linkToken) => {
  if (linkToken && sessionStorage.getItem(LINK_KEY) !== linkToken) {
    sessionStorage.setItem(LINK_KEY, linkToken);
    sessionStorage.setItem(DISPLAY_KEY, randomDisplayId());
  }
  return sessionStorage.getItem(DISPLAY_KEY);
};
export const linkToken = () => sessionStorage.getItem(LINK_KEY);
export const linkFor = (pathParam) =>
  pathParam && pathParam === sessionStorage.getItem(DISPLAY_KEY) ? linkToken() : pathParam || null;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const storedSession = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY));
    return stored && typeof stored === 'object' ? stored : {};
  } catch {
    return {};
  }
};

const sessionToken = () => {
  const { link, token } = storedSession();
  return link && link === linkToken() ? token : null;
};

export const statusOf = (error) => error?.response?.status;
export const messageOf = (error) => error?.response?.data?.message || error?.message || '';

const sanitize = (error) => {
  const clean = new Error(messageOf(error));
  if (error?.response) {
    clean.response = { status: error.response.status, data: error.response.data };
  }
  return clean;
};

export async function openSession(link) {
  try {
    const { data } = await client.post('/checkout/sessions', { link_token: link });
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ link, token: data.data.session_token }));
    return data.data;
  } catch (error) {
    throw sanitize(error);
  }
}

const attempt = async (call) => {
  try {
    return await call(sessionToken());
  } catch (error) {
    throw sanitize(error);
  }
};

async function withSession(call) {
  if (!sessionToken()) {
    await openSession(linkToken());
  }
  try {
    return await attempt(call);
  } catch (error) {
    if (statusOf(error) === 401 && linkToken()) {
      await openSession(linkToken());
      return attempt(call);
    }
    throw error;
  }
}

const read = (path) =>
  withSession((token) => client.get(path, auth(token)).then(({ data }) => data.data));

export const getSession = () => read('/checkout/sessions');
export const getNextAction = () => read('/checkout/next-action');
export const getPaymentMethods = () => read('/checkout/payment-methods');
export const getFinancing = () => read('/checkout/financing');

export const selectPaymentMethod = (body) =>
  withSession((token) =>
    client.put('/checkout/payment-method', body, auth(token)).then(({ data }) => data.data)
  );

export const savePayment = (body) =>
  withSession((token) =>
    client.put('/checkout/payment', body, auth(token)).then(({ data }) => data.data)
  );

export const submitConsent = (body) =>
  withSession((token) =>
    client.post('/checkout/financing/consent', body, auth(token)).then(({ data }) => data.data)
  );
